import {
  useRef,
  useState,
  useEffect,
} from 'react';
import Highlight, { defaultProps } from 'prism-react-renderer';
import rangeParser from 'parse-numeric-range';
import cn from 'classnames';
import CopyIcon from '../../assets/svg/copy.svg';
import useAnalyticsEvent from '../../hooks/useAnalyticsEvent';
import '../../assets/styles/codeblock.css';

const calculateLinesToHighlight = (raw) => {
  const lineNumbers = rangeParser(raw);

  if (lineNumbers) {
    return (index) => {
      return lineNumbers.includes(index + 1);
    }
  }

  return () => false;
};

const CodeBlock = ({ children: { props } }) => {
  const [showMsg, setShowMsg] = useState(false);
  const emitEvent = useAnalyticsEvent();
  const timeoutRef = useRef();

  const { className = '', children, highlight = '' } = props;
  const code = children?.trim();
  const language = className.replace(/language-/, '');
  const highlights = calculateLinesToHighlight(highlight);

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(code);

      setShowMsg(true);

      // Using window.location.pathname because the article props are not available
      // to use here easily. Would need to pass down from the markdown file itself, which is
      // too much.
      emitEvent({
        action: 'codeBlockCopyClick',
        category: 'codeBlock',
        label: window?.location?.pathname,
      });
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (showMsg) {
      timeoutRef.current = setTimeout(() => {
        setShowMsg(false);
      }, 2000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [showMsg]);

  return (
    <div className="code-block">
      <div className="code-block-inner">
        <button
          type="button"
          onClick={onClick}
          className="copy-btn"
          title="Copy code block"
        >
          {showMsg && <span>Copied!</span>}
          <CopyIcon />
        </button>
        <Highlight
          {...defaultProps}
          code={code}
          language={language}
          // Specifically set theme to undefined in order to use theme styles from
          // codeblock.css
          theme={undefined}
        >
          {({ className, style, tokens, getLineProps, getTokenProps }) => (
            <pre className={className} style={style}>
              {tokens.map((line, i) => {
                const { className: lineClassName, ...rest } = getLineProps({ line, key: i });
                return (
                  <div
                    className={cn(lineClassName, {
                      ['highlight-line']: highlights(i),
                    })}
                    {...rest}
                  >
                    {line.map((token, key) => (
                      <span {...getTokenProps({ token, key })} />
                    ))}
                  </div>
                )
              })}
            </pre>
          )}
        </Highlight>
      </div>
    </div>
  );
};

export default CodeBlock;
